<template>
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, isOn ? 'device-active--climate' : '']"
      :style="{ '--pin-active-color': iconAccentColor, borderColor: iconBorderColor }"
      :title="device.deviceName">
      <!-- The drawn water inside the status ring (P14): the ring carries the
           water temperature over the device's own setpoint span, with the
           numeric reading kept under the icon; a device that publishes no
           reading falls back to the plain on/off arc. The chemistry alert dot
           and the glow stay where they were. -->
      <ControlStatusRing :value="pinRingValue" :accent="iconAccentColor">
        <ControlIcon :name="isHotTub ? 'drop' : 'waves'" :size="pinIconSize" :style="{ color: iconIconColor }" />
        <span
          v-if="currentTemperature !== null"
          class="absolute -bottom-1 rounded-full bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800 shadow dark:bg-gray-900/90 dark:text-gray-100">
          {{ currentTemperature.toFixed(0) }}°
        </span>
      </ControlStatusRing>
      <div v-if="isOn" class="device-icon-pin__glow"></div>
      <div
        v-if="hasChemistryAlert"
        class="absolute right-0 top-0 h-3 w-3 rounded-full ring-2 ring-white dark:ring-gray-900"
        :style="{ backgroundColor: chemistryAlertColor }"></div>
    </div>
  </template>

  <template v-else-if="mode === 'grid'">
    <template v-if="hasChemistryAlert">
      <div
        class="w-full h-full rounded-xl border p-2 select-none cursor-pointer transition-colors"
        :style="gridChemistryStyle"
        @click="emit('open-control-modal')">
        <div class="flex h-full flex-col justify-between">
          <div class="flex items-center justify-between gap-2">
            <span class="material-symbols-outlined text-3xl" :style="{ color: chemistryAlertColor }">
              {{ defaultIcon }}
            </span>
            <span
              class="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
              :style="gridAlertBadgeStyle">
              {{ chemistryAlertLabel }}
            </span>
          </div>
          <div>
            <div class="truncate text-sm font-semibold text-white">{{ displayName }}</div>
            <div class="mt-0.5 text-xs text-gray-200">
              {{ gridChemistrySummary }}
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <DeviceGridCell
        :icon="device.icon || defaultIcon"
        :name="displayName"
        :is-on="isOn"
        :active-color="gridAccentColor"
        @toggle="emit('open-control-modal')"
        @long-press="emit('open-control-modal')" />
    </template>
  </template>

  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet (the host chrome owns the head and the panel):
         the hero — the water circle (tap = the same on/off write), the mode word
         and the water reading — then the setpoint with its stepper, the mode
         chips, the settable functions, the status tiles, the chemistry tiles
         and the meta line. -->
    <div class="spa-sheet" :style="{ '--sheet-accent': accentColor }">
      <div class="spa-hero">
        <button
          type="button"
          class="spa-circle"
          :class="{ 'spa-circle--active': isOn, 'spa-circle--busy': controlling }"
          :disabled="controlling"
          :aria-label="isOn ? tt('On', 'Vklopljeno') : tt('Off', 'Izklopljeno')"
          @click="togglePower">
          <span v-if="controlling" class="spa-spin" aria-hidden="true"></span>
          <ControlIcon v-else :name="isHotTub ? 'drop' : 'waves'" size="2.5rem" />
        </button>
        <div class="spa-state" :class="{ 'spa-state--active': isOn }">{{ operationModeLabel }}</div>
        <div class="spa-caption">{{ currentTemperatureLabel }}</div>
      </div>

      <!-- The setpoint: the target big, the stepper the existing write path
           (`adjustTemperature` — clamped to the device's min/max, rounded to its step). -->
      <div class="spa-target">
        <p class="spa-target-label">{{ temperatureLabel }}</p>
        <p class="spa-target-value">
          {{ targetTemperature !== null ? targetTemperature.toFixed(1) : '--' }}<small>°C</small>
        </p>
        <ControlStepper
          :value-text="targetStepperText"
          hold
          :disabled="controlling || targetTemperature === null"
          @step="stepTemperature" />
      </div>

      <!-- The modes this device offers (operation_mode), one chip each. -->
      <div v-if="modeOptions.length" class="spa-row">
        <div class="spa-row-head">
          <span class="spa-row-key">{{ tt('Mode', 'Način') }}</span>
        </div>
        <div class="spa-chips">
          <ControlChip
            v-for="option in modeOptions"
            :key="String(option.value)"
            :active="String(operationModeValue) === String(option.value)"
            :disabled="controlling"
            @click="setOperationMode(option.value)">
            {{ modeOptionLabel(option) }}
          </ControlChip>
        </div>
      </div>

      <!-- The settable functions (heating / jets / bubbles where the device
           exposes them as controls) — one toggle row each, same boolean write. -->
      <div v-if="controlPills.length" class="spa-row">
        <div class="spa-row-head">
          <span class="spa-row-key">{{ tt('Functions', 'Funkcije') }}</span>
        </div>
        <div class="spa-toggles">
          <ControlToggleRow
            v-for="pill in controlPills"
            :key="pill.key"
            :model-value="pill.active"
            :label="pill.label"
            :icon="functionIcon(pill.key)"
            :disabled="controlling"
            @update:model-value="toggleBooleanValue(pill.key)" />
        </div>
      </div>

      <!-- The functions that are read-only on this device — status, not controls. -->
      <div v-if="statusTiles.length" class="spa-tiles">
        <ControlValueTile
          v-for="tile in statusTiles"
          :key="tile.key"
          :icon="functionIcon(tile.key)"
          :label="tile.label"
          :value="tile.active ? tt('On', 'Vklopljeno') : tt('Off', 'Izklopljeno')"
          :tone="tile.active ? 'accent' : undefined" />
      </div>

      <!-- Water chemistry: one tile per metric, tinted by its severity; the
           ideal range rides under it. -->
      <div v-if="waterMetrics.length" class="spa-row">
        <div class="spa-row-head">
          <span class="spa-row-key">{{ tt('Water Chemistry', 'Kemija vode') }}</span>
        </div>
        <div class="spa-metrics">
          <div
            v-for="metric in waterMetrics"
            :key="metric.key"
            class="spa-metric"
            :style="{ '--sheet-accent': chemistrySeverityColor(metric.severity) }">
            <ControlValueTile
              :icon="chemistryIcon(metric.key)"
              :label="metric.label"
              :value="metric.displayValue"
              tone="accent" />
            <p v-if="metric.hint" class="spa-metric-hint">{{ metric.hint }}</p>
          </div>
        </div>
      </div>

      <p v-if="lastUpdated" class="spa-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlStepper from '../device-sheet/ControlStepper.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { readField } from './deviceWidgetTypes';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceValueOptionsStore } from '../../stores/deviceValueOptions';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();
const deviceValueOptionsStore = useDeviceValueOptionsStore();

const fetchedTargetOptions = ref<{ minValue: number | null; maxValue: number | null; step: number | null } | null>(
  null,
);
const fetchedModeOptions = ref<Array<{ value: number | string; label: string }> | null>(null);

type WaterMetricSeverity = 'good' | 'warn' | 'critical' | 'neutral';

interface WaterMetricCard {
  key: string;
  label: string;
  displayValue: string;
  severity: WaterMetricSeverity;
  hint?: string;
}

const PH_KEYS = ['ph', 'ph_value', 'ph_level'];
const ORP_KEYS = ['orp', 'orp_value', 'redox', 'redox_value'];
const SALINITY_KEYS = ['salinity', 'salinity_ppm', 'salt_level', 'salt', 'salinity_value'];
const FILTRATION_KEYS = [
  'filtration_active',
  'filter_active',
  'filter_status',
  'filtration_status',
  'circulation_active',
  'circulation_status',
];

const deviceSettings = computed<Record<string, unknown>>(() => {
  const settings = props.device.settings;
  return settings && typeof settings === 'object' && !Array.isArray(settings)
    ? (settings as Record<string, unknown>)
    : {};
});

const rawType = computed(() =>
  String(props.device.deviceControl || props.device.controlType || props.device.deviceType || '').toLowerCase(),
);
const isHotTub = computed(
  () => rawType.value.includes('hot_tub') || rawType.value.includes('hottub') || rawType.value.includes('jacuzzi'),
);

const accentColor = computed(() => (isHotTub.value ? 'rgb(249, 115, 22)' : 'rgb(14, 165, 233)'));
const gridAccentColor = computed(() => (isHotTub.value ? 'orange-400' : 'sky-400'));
const defaultIcon = computed(() => props.device.icon || (isHotTub.value ? 'hot_tub' : 'pool'));
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const temperatureLabel = computed(() =>
  isHotTub.value ? tt('Water Setpoint', 'Nastavitev vode') : tt('Pool Setpoint', 'Nastavitev bazena'),
);

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));
const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getSettingNumber(key: string, fallback: number): number {
  const numeric = toFiniteNumber(deviceSettings.value[key]);
  return numeric ?? fallback;
}

function getBooleanValue(keys: string[]): boolean {
  for (const key of keys) {
    if (key in currentValues.value) {
      const value = currentValues.value[key];
      return value === true || value === 1 || value === 'on' || value === 'true';
    }
  }

  const deviceValue = props.device.deviceValues?.find((value) => keys.includes(value.valueKey));
  const raw = deviceValue?.currentValue;
  return raw === true || raw === 1 || raw === 'on' || raw === 'true';
}

function getRawValue(keys: string[]): unknown {
  for (const key of keys) {
    if (key in currentValues.value) {
      return currentValues.value[key];
    }
  }

  const deviceValue = props.device.deviceValues?.find((value) => keys.includes(value.valueKey));
  return deviceValue?.currentValue;
}

function getNumericValue(keys: string[]): number | null {
  for (const key of keys) {
    if (key in currentValues.value) {
      const numeric = toFiniteNumber(currentValues.value[key]);
      if (numeric !== null) return numeric;
    }
  }

  const deviceValue = props.device.deviceValues?.find((value) => keys.includes(value.valueKey));
  return toFiniteNumber(deviceValue?.currentValue);
}

function getTextValue(keys: string[]): string | null {
  const rawValue = getRawValue(keys);
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return null;
  }
  return String(rawValue);
}

function hasValueKey(key: string): boolean {
  return Boolean(props.device.deviceValues?.some((value) => value.valueKey === key));
}

function getValueDefinition(key: string): DeviceValueWithReferences | null {
  return props.device.deviceValues?.find((value) => value.valueKey === key) ?? null;
}

function getValueDefinitionByKeys(keys: string[]): DeviceValueWithReferences | null {
  for (const key of keys) {
    const value = getValueDefinition(key);
    if (value) return value;
  }
  return null;
}

function getDisplayUnit(keys: string[], fallback = ''): string {
  const unit = getValueDefinitionByKeys(keys)?.unit;
  return typeof unit === 'string' && unit.trim().length > 0 ? unit.trim() : fallback;
}

const powerValueKey = computed(() => {
  for (const key of ['on_off', 'power', 'switch', 'state']) {
    if (hasValueKey(key)) return key;
  }
  return 'on_off';
});

const targetValueDef = computed(() => {
  for (const key of ['target_temperature', 'setpoint']) {
    const value = getValueDefinition(key);
    if (value) return value;
  }
  return null;
});

const targetValueKey = computed(() => targetValueDef.value?.valueKey || 'target_temperature');
const modeValueDef = computed(() => getValueDefinition('operation_mode'));

watch(
  () => props.device.deviceValues,
  (values) => {
    if (values?.length) {
      initializeValues(values);
      void loadOptions();
    }
  },
  { immediate: true, deep: true },
);

const isOn = computed(() => getBooleanValue([powerValueKey.value, 'on_off', 'power', 'switch', 'state']));
const currentTemperature = computed(() => getNumericValue(['current_temperature', 'temperature']));
const targetTemperature = computed(() => getNumericValue([targetValueKey.value, 'target_temperature', 'setpoint']));
const currentTemperatureLabel = computed(() =>
  currentTemperature.value !== null
    ? tt(`${currentTemperature.value.toFixed(1)}°C water`, `${currentTemperature.value.toFixed(1)}°C vode`)
    : tt('Water temperature unavailable', 'Temperatura vode ni na voljo'),
);

/** The stepper's reading — the same number the hero shows, with its unit. */
const targetStepperText = computed(() =>
  targetTemperature.value !== null ? `${targetTemperature.value.toFixed(1)} °C` : '--',
);

const operationModeValue = computed(() => {
  const raw = currentValues.value['operation_mode'] ?? modeValueDef.value?.currentValue;
  return raw ?? null;
});

/** A mode option: this file's own pair, or a label the hub sent as its data. */
type ModeOption = { value: number | string; label: string | [string, string] };

/** A mode option's label in the reader's language — this file's pair, or the hub's own word. */
function modeOptionLabel(option: ModeOption): string {
  return Array.isArray(option.label) ? tt(option.label[0], option.label[1]) : option.label;
}

/**
 * The four modes this widget knows by itself, in the app's own pair shape
 * (`label: ['English', 'Slovensko']`). A hub that sends its own enum labels
 * (`fetchedModeOptions`) is left exactly as it comes — those words are its data,
 * not this build's copy.
 */
const fallbackModeOptions = computed<ModeOption[]>(() => {
  if (isHotTub.value) {
    return [
      { value: 0, label: ['Off', 'Izklopljeno'] },
      { value: 1, label: ['Ready', 'Pripravljeno'] },
      { value: 2, label: ['Rest', 'Počitek'] },
      { value: 3, label: ['Eco', 'Eko'] },
    ];
  }

  return [
    { value: 0, label: ['Off', 'Izklopljeno'] },
    { value: 1, label: ['Auto', 'Samodejno'] },
    { value: 2, label: ['Heat', 'Gretje'] },
    { value: 3, label: ['Eco', 'Eko'] },
  ];
});

const modeOptions = computed(() =>
  fetchedModeOptions.value?.length ? fetchedModeOptions.value : fallbackModeOptions.value,
);

const operationModeLabel = computed(() => {
  const value = operationModeValue.value;
  const option = modeOptions.value.find((item) => String(item.value) === String(value));
  if (option) return modeOptionLabel(option);
  if (!isOn.value) return tt('Off', 'Izklopljeno');
  return value === null || value === undefined ? tt('Active', 'Aktivno') : String(value);
});

function evaluatePhSeverity(value: number): WaterMetricSeverity {
  if (value >= phMinThreshold.value && value <= phMaxThreshold.value) return 'good';
  if (value >= phWarnMin.value && value <= phWarnMax.value) return 'warn';
  return 'critical';
}

function evaluateOrpSeverity(value: number): WaterMetricSeverity {
  if (value >= orpMinThreshold.value && value <= orpMaxThreshold.value) return 'good';
  if (value >= orpWarnMin.value && value <= orpWarnMax.value) return 'warn';
  return 'critical';
}

function evaluateSalinitySeverity(value: number): WaterMetricSeverity {
  if (value >= salinityMinThreshold.value && value <= salinityMaxThreshold.value) return 'good';
  if (value >= salinityWarnMin.value && value <= salinityWarnMax.value) return 'warn';
  return 'critical';
}

const phMinThreshold = computed(() => getSettingNumber('ph_min_threshold', isHotTub.value ? 7.2 : 7.2));
const phMaxThreshold = computed(() => getSettingNumber('ph_max_threshold', isHotTub.value ? 7.8 : 7.6));
const phWarnMin = computed(() => phMinThreshold.value - 0.2);
const phWarnMax = computed(() => phMaxThreshold.value + 0.2);

const orpMinThreshold = computed(() => getSettingNumber('orp_min_threshold', 650));
const orpMaxThreshold = computed(() => getSettingNumber('orp_max_threshold', isHotTub.value ? 800 : 750));
const orpWarnMin = computed(() => orpMinThreshold.value - 50);
const orpWarnMax = computed(() => orpMaxThreshold.value + 50);

const salinityMinThreshold = computed(() => getSettingNumber('salinity_min_threshold', isHotTub.value ? 2500 : 3000));
const salinityMaxThreshold = computed(() => getSettingNumber('salinity_max_threshold', isHotTub.value ? 4500 : 4000));
const salinityWarnMin = computed(() => salinityMinThreshold.value - 500);
const salinityWarnMax = computed(() => salinityMaxThreshold.value + 500);

function evaluateStatusSeverity(text: string, active: boolean): WaterMetricSeverity {
  const normalized = text.toLowerCase();
  if (active || ['on', 'running', 'filtering', 'active', 'ready', 'auto'].includes(normalized)) {
    return 'good';
  }
  if (['off', 'stopped', 'idle', 'paused'].includes(normalized)) {
    return 'warn';
  }
  return 'neutral';
}

const phMetric = computed<WaterMetricCard | null>(() => {
  const value = getNumericValue(PH_KEYS);
  if (value === null) return null;
  return {
    key: 'ph',
    label: 'pH',
    displayValue: value.toFixed(2),
    severity: evaluatePhSeverity(value),
    hint: tt(
      `Ideal ${phMinThreshold.value.toFixed(1)}-${phMaxThreshold.value.toFixed(1)}`,
      `Idealno ${phMinThreshold.value.toFixed(1)}-${phMaxThreshold.value.toFixed(1)}`,
    ),
  };
});

const orpMetric = computed<WaterMetricCard | null>(() => {
  const value = getNumericValue(ORP_KEYS);
  if (value === null) return null;
  const unit = getDisplayUnit(ORP_KEYS, 'mV');
  return {
    key: 'orp',
    label: getValueDefinitionByKeys(ORP_KEYS)?.valueKey?.includes('redox') ? 'Redox' : 'ORP',
    displayValue: `${value.toFixed(0)} ${unit}`,
    severity: evaluateOrpSeverity(value),
    hint: tt(
      `Ideal ${orpMinThreshold.value.toFixed(0)}-${orpMaxThreshold.value.toFixed(0)} ${unit}`,
      `Idealno ${orpMinThreshold.value.toFixed(0)}-${orpMaxThreshold.value.toFixed(0)} ${unit}`,
    ),
  };
});

const salinityMetric = computed<WaterMetricCard | null>(() => {
  const value = getNumericValue(SALINITY_KEYS);
  if (value === null) return null;
  const unit = getDisplayUnit(SALINITY_KEYS, 'ppm');
  return {
    key: 'salinity',
    label: tt('Salinity', 'Slanost'),
    displayValue: `${value.toFixed(0)} ${unit}`,
    severity: evaluateSalinitySeverity(value),
    hint: tt(
      `Ideal ${salinityMinThreshold.value.toFixed(0)}-${salinityMaxThreshold.value.toFixed(0)} ${unit}`,
      `Idealno ${salinityMinThreshold.value.toFixed(0)}-${salinityMaxThreshold.value.toFixed(0)} ${unit}`,
    ),
  };
});

const filtrationMetric = computed<WaterMetricCard | null>(() => {
  const valueDefinition = getValueDefinitionByKeys(FILTRATION_KEYS);
  if (!valueDefinition) return null;

  const rawText = getTextValue(FILTRATION_KEYS);
  const isBoolean =
    rawText === 'true' ||
    rawText === 'false' ||
    rawText === '1' ||
    rawText === '0' ||
    typeof getRawValue(FILTRATION_KEYS) === 'boolean';
  const active = getBooleanValue(FILTRATION_KEYS);
  const label = isHotTub.value ? tt('Circulation', 'Kroženje') : tt('Filtration', 'Filtracija');
  const displayValue = isBoolean
    ? active
      ? tt('Running', 'Deluje')
      : tt('Stopped', 'Ustavljeno')
    : (rawText ?? tt('Unknown', 'Neznano'));

  return {
    key: valueDefinition.valueKey,
    label,
    displayValue,
    severity: evaluateStatusSeverity(displayValue, active),
    hint: isHotTub.value
      ? tt('Loop status', 'Stanje kroženja')
      : tt('Filter loop status', 'Stanje filtracijskega kroga'),
  };
});

const chemistryMetrics = computed<WaterMetricCard[]>(
  () => [phMetric.value, orpMetric.value, salinityMetric.value].filter(Boolean) as WaterMetricCard[],
);

const waterMetrics = computed<WaterMetricCard[]>(() => {
  const metrics = [...chemistryMetrics.value];
  if (filtrationMetric.value) metrics.push(filtrationMetric.value);
  return metrics;
});

function getTargetMetaNumber(keys: string[], fallback: number): number {
  for (const source of [fetchedTargetOptions.value, targetValueDef.value]) {
    if (!source) continue;
    for (const key of keys) {
      const numeric = toFiniteNumber(readField(source, key));
      if (numeric !== null) return numeric;
    }
  }
  return fallback;
}

function severityRank(severity: WaterMetricSeverity): number {
  switch (severity) {
    case 'critical':
      return 3;
    case 'warn':
      return 2;
    case 'good':
      return 1;
    default:
      return 0;
  }
}

const chemistryAlertSeverity = computed<WaterMetricSeverity | null>(() => {
  const alertMetrics = chemistryMetrics.value.filter(
    (metric) => metric.severity === 'critical' || metric.severity === 'warn',
  );

  if (!alertMetrics.length) {
    return null;
  }

  return alertMetrics.reduce<WaterMetricSeverity>(
    (current, metric) => (severityRank(metric.severity) > severityRank(current) ? metric.severity : current),
    'warn',
  );
});

const hasChemistryAlert = computed(() => chemistryAlertSeverity.value !== null);
const chemistryAlertColor = computed(() => chemistrySeverityColor(chemistryAlertSeverity.value ?? 'neutral'));
const chemistryAlertLabel = computed(() =>
  chemistryAlertSeverity.value === 'critical' ? tt('Alert', 'Opozorilo') : tt('Monitor', 'Spremljanje'),
);

const iconAccentColor = computed(() => (hasChemistryAlert.value ? chemistryAlertColor.value : accentColor.value));
const iconBorderColor = computed(() => (isOn.value || hasChemistryAlert.value ? iconAccentColor.value : ''));
const iconIconColor = computed(() => (isOn.value || hasChemistryAlert.value ? iconAccentColor.value : ''));

/**
 * The pin's ring (P14): the water temperature over the device's own setpoint
 * span (the target's min…max — the hot tub's 30…42 °C, a pool's 10…40 °C,
 * either from the fetched options or those fallbacks). A device with no
 * temperature reading falls back to the plain on/off arc.
 */
const pinRingValue = computed(() => {
  const reading = currentTemperature.value;
  if (reading !== null && TEMP_MAX.value > TEMP_MIN.value) {
    const pct = ((reading - TEMP_MIN.value) / (TEMP_MAX.value - TEMP_MIN.value)) * 100;
    return Math.max(0, Math.min(100, pct));
  }
  return isOn.value ? 100 : 0;
});

/** The drawn water's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

const gridChemistrySummary = computed(() => {
  const metric =
    chemistryMetrics.value.find((entry) => entry.severity === 'critical') ??
    chemistryMetrics.value.find((entry) => entry.severity === 'warn') ??
    chemistryMetrics.value[0];

  if (!metric) {
    return currentTemperatureLabel.value;
  }

  return `${metric.label}: ${metric.displayValue}`;
});

const gridChemistryStyle = computed(() => ({
  background: `linear-gradient(160deg, ${chemistryAlertColor.value}22 0%, rgba(17, 24, 39, 0.9) 100%)`,
  borderColor: `${chemistryAlertColor.value}88`,
  boxShadow: `0 0 0 1px ${chemistryAlertColor.value}22 inset`,
}));

const gridAlertBadgeStyle = computed(() => ({
  backgroundColor: `${chemistryAlertColor.value}18`,
  border: `1px solid ${chemistryAlertColor.value}66`,
  color: chemistryAlertColor.value,
}));

const TEMP_MIN = computed(() => getTargetMetaNumber(['minValue', 'min_value', 'min'], isHotTub.value ? 30 : 10));
const TEMP_MAX = computed(() => getTargetMetaNumber(['maxValue', 'max_value', 'max'], isHotTub.value ? 42 : 40));
const tempStep = computed(() => {
  const step = getTargetMetaNumber(['step', 'deltaValue', 'delta_value'], 0.5);
  return step > 0 ? step : 0.5;
});

function roundToStep(value: number, step: number): number {
  const precision = String(step).split('.')[1]?.length ?? 0;
  return Number((Math.round(value / step) * step).toFixed(precision));
}

const statusPills = computed(() => {
  const pills = [] as Array<{ key: string; label: string; active: boolean }>;
  if (hasValueKey('heating_active'))
    pills.push({ key: 'heating_active', label: tt('Heating', 'Gretje'), active: getBooleanValue(['heating_active']) });
  if (hasValueKey('jets_active'))
    pills.push({ key: 'jets_active', label: tt('Jets', 'Šobe'), active: getBooleanValue(['jets_active']) });
  if (hasValueKey('bubble_active'))
    pills.push({ key: 'bubble_active', label: tt('Bubbles', 'Mehurčki'), active: getBooleanValue(['bubble_active']) });
  return pills;
});

const controlPills = computed(() =>
  statusPills.value.filter((pill) => {
    const definition = getValueDefinition(pill.key);
    return Boolean(definition?.modeSettable);
  }),
);

/**
 * The status readings that are NOT controls and are not already covered by a
 * chemistry tile (the circulation / filtration loop rides there) — the
 * read-only half of the sheet, rendered as tiles.
 */
const statusTiles = computed(() => {
  const metricKeys = new Set(waterMetrics.value.map((metric) => metric.key));
  return statusPills.value.filter(
    (pill) => !controlPills.value.some((control) => control.key === pill.key) && !metricKeys.has(pill.key),
  );
});

/** The ControlIcon for a function key (heating / jets / bubbles). */
function functionIcon(key: string): string {
  switch (key) {
    case 'heating_active':
      return 'flame';
    case 'jets_active':
      return 'waves';
    case 'bubble_active':
      return 'sparkles';
    default:
      return 'drop';
  }
}

/** The ControlIcon for a chemistry metric — the filtration loop gets the filter. */
function chemistryIcon(key: string): string {
  switch (key) {
    case 'ph':
      return 'drop';
    case 'orp':
      return 'bolt';
    case 'salinity':
      return 'sparkles';
    default:
      return 'filter';
  }
}

function chemistrySeverityColor(severity: WaterMetricSeverity): string {
  switch (severity) {
    case 'good':
      return 'rgb(34, 197, 94)';
    case 'warn':
      return 'rgb(245, 158, 11)';
    case 'critical':
      return 'rgb(239, 68, 68)';
    default:
      return 'rgb(107, 114, 128)';
  }
}

async function loadOptions() {
  if (targetValueDef.value?.id) {
    const options = await deviceValueOptionsStore.fetchOptions(targetValueDef.value.id);
    fetchedTargetOptions.value = {
      minValue: toFiniteNumber(options?.min_value ?? options?.minValue),
      maxValue: toFiniteNumber(options?.max_value ?? options?.maxValue),
      step: toFiniteNumber(options?.step ?? options?.delta_value ?? options?.deltaValue),
    };
  }

  if (modeValueDef.value?.id) {
    const options = await deviceValueOptionsStore.fetchOptions(modeValueDef.value.id);
    fetchedModeOptions.value = Array.isArray(options?.enum_options)
      ? options.enum_options.map((item) => ({ value: item.value as number | string, label: item.label }))
      : null;
  }
}

async function togglePower() {
  await setProperty(props.device, powerValueKey.value, !isOn.value);
}

async function adjustTemperature(delta: number) {
  const current = targetTemperature.value ?? TEMP_MIN.value;
  const next = Math.max(TEMP_MIN.value, Math.min(TEMP_MAX.value, roundToStep(current + delta, tempStep.value)));
  await setProperty(props.device, targetValueKey.value, next);
}

async function stepTemperature(delta: 1 | -1) {
  await adjustTemperature(delta * tempStep.value);
}

async function setOperationMode(value: number | string) {
  await setProperty(props.device, 'operation_mode', value);
}

async function toggleBooleanValue(key: string) {
  await setProperty(props.device, key, !getBooleanValue([key]));
}
</script>

<style scoped>
/* The tiles' own layout: the head at the top, chips and the water readings
   under it, so a hot tub reads like the tiles beside it. */
.spa-button-layout {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.4em;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  /* Space for the menu button and the slider */
  padding-right: 1em;
  text-align: left;
}

.spa-button-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25em;
}

/* Chips size off the tile's own font size, so they scale with the tile */
.spa-button-chip {
  border-radius: 0.5em;
  padding: 0.15em 0.5em;
  font-size: 0.55em;
  font-weight: 500;
  line-height: 1.1;
  white-space: nowrap;
}

/* The room's own reading rides beside the setpoint: smaller and quieter */
.spa-button-current {
  margin-left: 0.4em;
  font-size: 0.68em;
  font-weight: 600;
  color: rgb(107 114 128);
}

.dark .spa-button-current {
  color: rgb(156 163 175);
}

.spa-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25em;
}

.spa-metric {
  border: 1px solid transparent;
  border-radius: 0.5em;
  padding: 0.25em 0.5em;
  font-size: 0.5em;
  line-height: 1.2;
}
</style>

<style scoped>
/* Control mode: the sheet body (the host chrome owns the head and the panel).
   Every colour reads `--sheet-accent`, which the body root sets to the
   device's own colour (pool blue / hot tub amber). */
.spa-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
}

.spa-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}

/* The one big control: a 6rem circle, tinted by the device's accent while the
   water is on; the busy state calms it while a write is in flight. */
.spa-circle {
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
}

.dark .spa-circle {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-400));
}

.spa-circle:hover:not(:disabled) {
  transform: translateY(-1px);
}

.spa-circle--active {
  border-color: color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent) 13%, transparent);
  color: var(--sheet-accent);
  box-shadow: 0 0 24px color-mix(in srgb, var(--sheet-accent) 22%, transparent);
}

.spa-circle--busy {
  opacity: 0.7;
}

.spa-circle:disabled {
  cursor: default;
}

.spa-spin {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--sheet-accent) 35%, transparent);
  border-top-color: var(--sheet-accent);
  animation: spa-spin 0.8s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .spa-spin {
    animation-duration: 2.4s;
  }
}

@keyframes spa-spin {
  to {
    transform: rotate(360deg);
  }
}

.spa-state {
  font-size: 1.0625rem;
  font-weight: 700;
  color: rgb(var(--gray-600));
}

.dark .spa-state {
  color: rgb(var(--gray-200));
}

.spa-state--active {
  color: var(--sheet-accent);
}

.spa-caption {
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .spa-caption {
  color: rgb(var(--gray-400));
}

/* The setpoint block: the label, the big target, the stepper. */
.spa-target {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: calc(0.875rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.1);
}

.dark .spa-target {
  background: rgba(255, 255, 255, 0.04);
}

.spa-target-label {
  margin: 0;
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .spa-target-label {
  color: rgb(var(--gray-400));
}

.spa-target-value {
  margin: 0;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--sheet-accent);
  font-variant-numeric: tabular-nums;
}

.spa-target-value small {
  margin-left: 0.15rem;
  font-size: 1.125rem;
  font-weight: 600;
}

/* Section rows: a key line over the controls. */
.spa-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.spa-row-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.spa-row-key {
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .spa-row-key {
  color: rgb(var(--gray-400));
}

.spa-chips {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.375rem * var(--ui-space-factor));
}

.spa-toggles {
  display: flex;
  flex-direction: column;
}

.spa-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* Chemistry: one tile per metric under a severity-tinted accent, the ideal
   range riding under it. */
.spa-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

.spa-metric {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.spa-metric-hint {
  margin: 0;
  padding-left: 0.75rem;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .spa-metric-hint {
  color: rgb(var(--gray-400));
}

.spa-meta {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375em;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .spa-meta {
  color: rgb(var(--gray-400));
}
</style>
